import type { MouseEvent } from 'react'
import type { InboxItem } from '@/hooks/use-inbox'
import type { Flow, FlowTrigger } from '@/lib/api'
import type { Workspace } from '@/providers/workspace-provider'
import type { BasecampAssignment, BasecampProject } from '@/types/basecamp'

/** Compact flow fields for Home preview (from list payload). */
export type HomeFlowSummary = {
  enabled: boolean
  paused: boolean
  pausedReason: string | null
  lastRunAt: string | null
  lastRunStatus: string | null
  triggers: FlowTrigger[]
  tags: string[]
  health?: string
}

/** Cross-widget selection on Global Home (Continuity). */
export type HomeSelection =
  | { kind: 'document'; id: string; title: string; path?: string }
  | { kind: 'inbox'; item: InboxItem }
  | { kind: 'chat'; threadId: string; title: string }
  | {
      kind: 'flow'
      id: string
      title: string
      path?: string
      summary?: HomeFlowSummary
    }
  | {
      kind: 'suggestion'
      id: string
      title: string
      documentId?: string
      path?: string
      actionType: string
      actionId: string
      description?: string
    }
  | {
      kind: 'basecamp-todo'
      assignment: BasecampAssignment
    }
  | {
      kind: 'basecamp-project'
      project: BasecampProject
    }
// Future Matter Feed widget:
// | { kind: 'feed'; id: string; title: string; topic?: string; url?: string }

export const HOME_PREVIEW_STORAGE_KEY = 'home.previewOpen'

export function homeSelectionKey(selection: HomeSelection | null): string | null {
  if (!selection) return null
  switch (selection.kind) {
    case 'document':
      return `document:${selection.id}`
    case 'inbox':
      return `inbox:${selection.item.id}`
    case 'chat':
      return `chat:${selection.threadId}`
    case 'flow':
      return `flow:${selection.id}`
    case 'suggestion':
      return `suggestion:${selection.actionType}:${selection.actionId}`
    case 'basecamp-todo':
      return `basecamp-todo:${selection.assignment.id}`
    case 'basecamp-project':
      return `basecamp-project:${selection.project.id}`
  }
}

/** Document UUID when the selection can drive DocumentPreviewPane + Add to chat. */
export function documentIdFromHomeSelection(selection: HomeSelection | null): string | null {
  if (!selection) return null
  if (selection.kind === 'document') return selection.id
  if (selection.kind === 'suggestion' && selection.documentId) return selection.documentId
  if (selection.kind === 'inbox') {
    return selection.item.actions?.find((a) => a.openDocumentId)?.openDocumentId ?? null
  }
  if (selection.kind === 'basecamp-todo') {
    return selection.assignment._flownotes_document_id ?? null
  }
  return null
}

export function titleFromHomeSelection(selection: HomeSelection | null): string {
  if (!selection) return ''
  if (selection.kind === 'inbox') return selection.item.title
  if (selection.kind === 'chat') return selection.title
  if (selection.kind === 'basecamp-todo') return selection.assignment.title
  if (selection.kind === 'basecamp-project') {
    return selection.project.name || 'Basecamp Project'
  }
  return selection.title
}

export function pathFromHomeSelection(selection: HomeSelection | null): string | undefined {
  if (!selection) return undefined
  if (selection.kind === 'document' || selection.kind === 'flow' || selection.kind === 'suggestion') {
    return selection.path
  }
  if (selection.kind === 'basecamp-todo') {
    return selection.assignment._workspec_file_path ?? undefined
  }
  return undefined
}

export function metaFromHomeSelection(selection: HomeSelection | null): string | null {
  if (!selection) return null
  switch (selection.kind) {
    case 'document':
      return 'Document'
    case 'inbox':
      return [selection.item.source, selection.item.type].filter(Boolean).join(' · ') || 'Inbox'
    case 'chat':
      return 'Chat'
    case 'flow':
      return 'Flow'
    case 'suggestion':
      return selection.description || 'Suggestion'
    case 'basecamp-todo': {
      const projectName =
        selection.assignment.project?.name ||
        selection.assignment.project_name ||
        selection.assignment._project_name ||
        'Basecamp'
      return projectName
    }
    case 'basecamp-project':
      return selection.project.bookmarked ? 'Bookmarked project' : 'Basecamp project'
  }
}

export function inboxDocumentId(item: InboxItem): string | null {
  return item.actions?.find((a) => a.openDocumentId)?.openDocumentId ?? null
}

export function flowToSelection(flow: Flow): HomeSelection {
  return {
    kind: 'flow',
    id: flow.id,
    title: flow.title,
    path: flow.file_path || undefined,
    summary: {
      enabled: flow.enabled,
      paused: flow.paused,
      pausedReason: flow.paused_reason,
      lastRunAt: flow.last_run_at,
      lastRunStatus: flow.last_run_status,
      triggers: flow.triggers ?? [],
      tags: flow.tags ?? [],
      health: flow.health,
    },
  }
}

export function basecampTodoToSelection(assignment: BasecampAssignment): HomeSelection {
  return { kind: 'basecamp-todo', assignment }
}

export function basecampProjectToSelection(project: BasecampProject): HomeSelection {
  return { kind: 'basecamp-project', project }
}

export function readHomePreviewOpen(): boolean {
  try {
    return sessionStorage.getItem(HOME_PREVIEW_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function writeHomePreviewOpen(open: boolean): void {
  try {
    sessionStorage.setItem(HOME_PREVIEW_STORAGE_KEY, open ? '1' : '0')
  } catch {
    /* ignore */
  }
}

/** Props passed from GlobalHomePage into index-like feed widgets. */
export type HomeContinuityProps = {
  selectedKey: string | null
  selection: HomeSelection | null
  previewOpen: boolean
  /** True when 2+ active workspaces - show colored left rails on feed rows. */
  showWorkspaceRails: boolean
  selectHome: (
    next: HomeSelection,
    opts?: { openPreview?: boolean; flow?: Flow },
  ) => void
  openSelection: (sel: HomeSelection | null, event?: MouseEvent) => void
  workspaces: Workspace[]
}
